import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import RefundsTable from "./components/RefundsTable";
import AddRefundModal from "./components/AddRefundModal";
import CreditNotesTable from "./components/CreditNotesTable";
import StatCard from "components/ui/StatCard";
import LoadingSkeleton from "./components/LoadingSkeleton";
import { useGetRefunds } from "./useGetRefunds";
import { useGetCreditNotes } from "./useGetCreditNotes";
import { useCreateRefund } from "./useCreateRefund";
import { useVoidRefund } from "./useVoidRefund";
import { useGetRefundClients } from "./useGetRefundClients";
import { sumAmount } from "./logic/stats";

const TABS = [
  { id: "refunds", label: "Refunds", icon: "RotateCcw" },
  { id: "credit-notes", label: "Credit Notes", icon: "FileMinusIcon" },
];

const MUTATIONS_ENABLED = true;

const RefundsContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const [activeTab, setActiveTab] = useState("refunds");

  const { data: refunds = [], isLoading: refundsLoading } = useGetRefunds();
  const { data: creditNotes = [], isLoading: creditNotesLoading } =
    useGetCreditNotes();
  const { data: refundClients = [] } = useGetRefundClients();
  const createRefundMutation = useCreateRefund();
  const voidRefundMutation = useVoidRefund();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddRefundOpen, setIsAddRefundOpen] = useState(false);
  const itemsPerPage = 10;

  // Reset paging + search when switching tabs
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm("");
  }, [activeTab]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const activeList = activeTab === "refunds" ? refunds : creditNotes;

  const filtered = activeList.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.clientName?.toLowerCase().includes(term) ||
      item.invoiceNumber?.toLowerCase().includes(term) ||
      (item.receiptNumber || item.creditNoteNumber || "")
        .toLowerCase()
        .includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = [
    {
      label: t("refunds.stats.totalRefunds"),
      value: refunds.length,
      icon: "RotateCcw",
      color: "text-red-500 bg-red-100 dark:bg-red-900/20 dark:text-red-400",
    },
    {
      label: t("refunds.stats.totalRefunded"),
      value: `SR ${sumAmount(refunds).toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
      icon: "TrendingDown",
      color:
        "text-orange-500 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
    },
    {
      label: t("refunds.stats.creditNotes"),
      value: creditNotes.length,
      icon: "FileMinusIcon",
      color: "text-blue-500 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
    },
    {
      label: t("refunds.stats.totalCreditValue"),
      value: `SR ${sumAmount(creditNotes).toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
      icon: "BadgeMinus",
      color:
        "text-violet-500 bg-violet-100 dark:bg-violet-900/20 dark:text-violet-400",
    },
  ];

  const handleCreateRefund = async (payload) => {
    try {
      await createRefundMutation.mutateAsync(payload);
      toast.success(t("refunds.refundRecorded"));
      setIsAddRefundOpen(false);
    } catch (error) {
      toast.error(error?.message || t("refunds.failedRecord"));
    }
  };

  const handleVoidRefund = async (
    refundId,
    reason = t("refunds.voidReason"),
  ) => {
    try {
      await voidRefundMutation.mutateAsync({
        id: refundId,
        reason,
      });
      toast.success(t("refunds.refundVoided"));
    } catch (error) {
      toast.error(error?.message || t("refunds.failedVoid"));
    }
  };

  if (refundsLoading || creditNotesLoading) {
    return <LoadingSkeleton isCollapsed={isCollapsed} />;
  }

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("refunds.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("refunds.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>

              <Button
                variant="default"
                size="md"
                iconName="Plus"
                onClick={() => setIsAddRefundOpen(true)}
              >
                {t("actions.addRefund")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* ── Stats Row ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </motion.div>

        {/* ── Tabs ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="mb-6"
        >
          <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit border border-border">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-card text-foreground shadow-sm border border-border"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon name={tab.icon} size={15} />
                {tab.id === "refunds"
                  ? t("status.refunds")
                  : t("status.creditNotes")}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === tab.id
                      ? "bg-primary/10 text-primary"
                      : "bg-muted-foreground/20 text-muted-foreground"
                  }`}
                >
                  {tab.id === "refunds" ? refunds.length : creditNotes.length}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                activeTab === "refunds"
                  ? t("refunds.searchRefunds")
                  : t("refunds.searchCreditNotes")
              }
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* ── Table ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "refunds" ? (
              <motion.div
                key="refunds"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <RefundsTable refunds={paginated} onVoid={handleVoidRefund} />
              </motion.div>
            ) : (
              <motion.div
                key="credit-notes"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CreditNotesTable
                  creditNotes={paginated}
                  onEdit={MUTATIONS_ENABLED ? () => {} : undefined}
                  onDelete={MUTATIONS_ENABLED ? () => {} : undefined}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ── Empty State ── */}
        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon
                name={activeTab === "refunds" ? "RotateCcw" : "FileMinusIcon"}
                size={32}
                className="text-primary"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("refunds.emptyTitle", {
                type:
                  activeTab === "refunds"
                    ? t("status.refunds")
                    : t("status.creditNotes"),
              })}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? t("common.adjustSearch")
                : t("refunds.emptyConnected")}
            </p>
          </motion.div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("common.showingRange", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filtered.length),
                total: filtered.length,
                entity:
                  activeTab === "refunds"
                    ? t("status.refunds")
                    : t("status.creditNotes"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {t("common.pageOf", { current: currentPage, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}

        <AnimatePresence>
          {isAddRefundOpen && (
            <AddRefundModal
              clients={refundClients}
              onSave={handleCreateRefund}
              onClose={() => setIsAddRefundOpen(false)}
              isSaving={createRefundMutation.isPending}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const RefundsPage = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <RefundsContent />
    </div>
  </SidebarProvider>
);

export default RefundsPage;
